import { auth, db, googleProvider } from "../plugins/firebase";
import { baseApiUrl } from "../environment/values";
import { axiosInstance as axios } from "../main";
import router from "../router";
import TabManager from "../services/tab-manager";
import { safeFirebaseSignOut } from "../helpers/firebaseAuth";

let isClearingSession = false;

export function getIsClearingSession() {
  return isClearingSession;
}

export const STATUS = {
  FAIL: "fail",
  SUCCES: "success",
  PENDING: "pending",
};

const getDefaultState = () => {
  return {
    authorized: false,
    accessWithAutologin: false,
    autologinStatus: undefined,
    needsPolicyAcceptance: false,
    hasAcceptedPolicy: false,
    rapiUser: null,
    firebaseUser: null,
    policyStatus: null,
  };
};

const state = {
  authorized: false,
  accessWithAutologin: false,
  autologinStatus: undefined,
  needsPolicyAcceptance: false,
  hasAcceptedPolicy: false,
  firebaseUser: null,
  rapiUser: null,
  policyStatus: null,
};

const LEGAL_DOCUMENTS_API = process.env.VUE_APP_LEGAL_DOCUMENTS_API_URL;

export const userHasTyCPPSigned = (email) => {
  return axios.get(
    `${LEGAL_DOCUMENTS_API}/v1/legal-documents/documents/check-signature/?email=${email}&origin=PRESTADOR`
  );
};

export const policyVersions = () => {
  return axios.get(
    `${LEGAL_DOCUMENTS_API}/v1/legal-documents/documents/latest/?origin=PRESTADOR`,
  );
};

/**
 * @param {{ email: string, document_hashes: string[], origin: string }} params
 * @param {object} headers - Optional headers to include in the request
 */
export const signPolicy = (params, headers = {}) => {
  return axios.post(
    `${LEGAL_DOCUMENTS_API}/v1/legal-documents/documents/sign/`,
    params,
    { headers }
  );
};

const getters = {
  signWithLink: (state) => {
    return (
      state.accessWithAutologin ||
      JSON.parse(localStorage.getItem("accessWithAutologin") || "false")
    );
  },
  needsPolicyAcceptance: (state) => state.needsPolicyAcceptance,
  hasAcceptedPolicy: (state) => state.hasAcceptedPolicy,
  policyStatus: (state) => state.policyStatus,
};
const actions = {
  async login({ dispatch, commit }, { email, password }) {
    try {
      await auth.signInWithEmailAndPassword(email, password);
      await dispatch("afterLoginFlow");
    } catch (error) {
      throw error;
    }
  },

  async loginWithGoogle({ dispatch }) {
    try {
      await auth.signOut();
      await auth.signInWithPopup(googleProvider);
      await dispatch("afterLoginFlow");
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  async checkPolicySignature({ commit }, user) {
    try {
      const email = user.email;
      const { data: policyData } = await userHasTyCPPSigned(email);

      const docs = policyData.documents;
      const transformedStatus = {
        ...policyData,
        already_signed: docs.some(d => d.already_signed),
        has_signed_tyc: docs.find(d => d.code === 'TYC')?.signed ?? false,
        has_signed_pg:  docs.find(d => d.code === 'PG')?.signed  ?? false,
      };
      commit("setPolicyStatus", transformedStatus);

      const existingUser = JSON.parse(localStorage.getItem("user") || "{}");

      const userWithPolicy = {
        ...existingUser,
        uid: user.uid,
        all_signed:     policyData.all_signed,
        already_signed: docs.some(d => d.already_signed),
        has_signed_tyc: docs.find(d => d.code === 'TYC')?.signed ?? false,
        has_signed_pg:  docs.find(d => d.code === 'PG')?.signed  ?? false,
      };

      localStorage.setItem("user", JSON.stringify(userWithPolicy));

      // Convert all_signed to boolean - check if it's explicitly true
      const allSignedIsTruthy =
        policyData.all_signed === true || policyData.all_signed === "true";

      if (!allSignedIsTruthy) {
        commit("setNeedsPolicyAcceptance", true);
        return { needsPolicyAcceptance: true };
      }

      commit("setNeedsPolicyAcceptance", false);
      return { needsPolicyAcceptance: false };
    } catch (error) {
      console.error("Error checking policy signature:", error);
      // Don't throw - assume policy needs acceptance if check fails
      commit("setNeedsPolicyAcceptance", false);
      return { needsPolicyAcceptance: false };
    }
  },

  async acceptUserPolicy({ commit, dispatch }, { params, headers }) {
    try {
      const user = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : null;

      const { data } = await signPolicy(params, headers);

      if (data.success && user) {
        user.all_signed = true;
        localStorage.setItem("user", JSON.stringify(user));

        commit("setNeedsPolicyAcceptance", false);
        commit("setHasAcceptedPolicy", true);

        await dispatch("afterLoginFlow");
      } else {
        throw new Error("Sign policy failed");
      }
    } catch (error) {
      console.error("Error accepting policy:", error);
    }
  },
  /**
   * @param {{ email: string, policy: string, version: string, origin: string }} params
   */
  async acceptPolicy({ commit }, params) {
    try {
      const { data } = await axios.post("/accept-policy/", params);
      return data;
    } catch (error) {
      console.error("Error accepting policy:", error);
      throw error;
    }
  },

  async autologin({ commit, state, dispatch }, key) {
    commit("setAccessWithAutologin", true);
    commit("setAutologinStatus", STATUS.PENDING);

    TabManager.broadcast("FORCE_LOGOUT");

    try {
      if (!key) {
        throw new Error("No se proporcionó la clave de autologin");
      }

      const urlAutologin = `${baseApiUrl}autologin/`;

      try {
        const response = await axios.post(urlAutologin, { key });
        commit("setRapiUser", response.data);
      } catch (error) {
        throw error;
      }

      await auth.signInWithCustomToken(state.rapiUser.firebase_custom_token);
      await dispatch("afterLoginFlow");

      commit("setAutologinStatus", STATUS.SUCCES);
    } catch (error) {
      commit("setAutologinStatus", STATUS.FAIL);

      if (error.isAxiosError) {
        const message = error.response.data?.detail ?? "Error desconocido";
        console.error("[Axios error]: " + message);
      } else if (error instanceof Error) {
        console.error("[Error]: " + error.message);
      } else {
        console.error("[Error desconocido]: " + error);
      }

      router.push({ name: "login-error" });
    }
  },
  async getUserSettings({ state, getters, dispatch }, user) {
    if (!user) {
      router.push({ name: "login" });
      return;
    }

    try {
      if (!state.hasAcceptedPolicy && !getters.signWithLink) {
        const policyCheckResult = await dispatch("checkPolicySignature", user);

        if (policyCheckResult?.needsPolicyAcceptance) {
          return;
        }
      }

      const { uid: userId } = user;
      const requestId = state.rapiUser?.request_id;

      const appSettings = db.ref(userId + "/settings");

      const snapshot = await appSettings.once("value");
      const userSettings = snapshot.val();

      localStorage.setItem("baseURL", JSON.stringify(baseApiUrl));
      localStorage.setItem("userSettings", JSON.stringify(userSettings));

      if (getters.signWithLink && requestId) {
        router.push({
          name: "request-detail",
          params: { id: requestId },
        });
      } else {
        router.push({ name: "visit-list" });
      }
    } catch (error) {
      console.error("Error getting user settings:", error);
      await router.push({ name: "visit-list" });
    }
  },

  async handleAuthStateChanged({ commit }, user) {
    if (!user) {
      if (!isClearingSession) {
        commit("setState");
      }
      return;
    }

    commit("setAuthorized", true);
    commit("setFirebaseUser", user);
    commit("setNeedsPolicyAcceptance", false);
    localStorage.setItem("user", JSON.stringify(user));

    let currentTime = new Date();
    currentTime = currentTime.getTime();
    localStorage.setItem("lastTokenRefreshTime", currentTime);
  },

  async afterLoginFlow({ dispatch }) {
    const user = auth.currentUser;
    await dispatch("getUserSettings", user);
  },

  async forceLogoutTab({ commit }) {
    commit("setState");
    router.push({ name: "login" });
  },

  async clearSession({ commit }) {
    if (isClearingSession) {
      return;
    }

    isClearingSession = true;

    try {
      commit("setState");

      const { requiresReload } = await safeFirebaseSignOut();

      localStorage.clear();

      if (requiresReload) {
        window.location.replace(router.resolve({ name: "login" }).href);
        return;
      }

      if (router.currentRoute.name !== "login") {
        await router.push({ name: "login" });
      }
    } finally {
      isClearingSession = false;
    }
  },
  // async storeTechnicianRequests({commit}) {
  //   const technicianInfoUrl = `${baseApiUrl}technician_info/`;
  //   let technicianInfo
  //   try {
  //     const response = await axios.get(technicianInfoUrl);
  //     const technicianData = response.data
  //     technicianInfo = technicianData
  //   } catch (error) {
  //     console.log("technician info failed", error);
  //     technicianInfo = []
  //   }
  //   commit("set_user_techReqs", technicianInfo)
  //   localStorage.setItem("technicianInfo", JSON.stringify(technicianInfo));
  // }
};
const mutations = {
  setAuthorized: (state, payload) => (state.authorized = payload),
  setAccessWithAutologin: (state, payload) => {
    state.accessWithAutologin = payload;
    localStorage.setItem("accessWithAutologin", payload);
  },
  setAutologinStatus: (state, payload) => (state.autologinStatus = payload),
  setNeedsPolicyAcceptance: (state, payload) =>
    (state.needsPolicyAcceptance = payload),
  setHasAcceptedPolicy: (state, payload) => (state.hasAcceptedPolicy = payload),
  setPolicyStatus: (state, payload) => (state.policyStatus = payload),
  setState: (state) => {
    Object.assign(state, getDefaultState());
    localStorage.removeItem("accessWithAutologin");
  },
  setFirebaseUser: (state, payload) => (state.firebaseUser = payload),
  setRapiUser: (state, payload) => (state.rapiUser = payload),
};

export default {
  state,
  getters,
  actions,
  mutations,
};
